/**
 * Transforms the raw GeoJSON features from the collection DSP endpoint into
 * UI-ready "plots" with a flat, classifier-ready stats object.
 *
 * ## The mental mapping
 *
 * This is the digital-sampling-plot counterpart to `collectionHexagonsToCells`,
 * and deliberately produces the same `{ id, geometry, detections, stats }` shape
 * so the shared classification/property/stat helpers work on either. The
 * endpoint returns one Feature per drone image, carrying per-object stats under
 * `properties.detections`:
 *
 *   { [objectKey]: { count, mean_height, mean_object_area, sum_object_area } }
 *
 * plus `properties.tile_name` (the plot's identity) and `properties.area_m2`
 * (that one image's footprint area, in EPSG:6933 — the plot's own sample size).
 *
 * Two differences from the hexagon path:
 *
 * 1. **`area` is per-plot, not a resolution.** Hexagon cells all share the
 *    requested cell size; a plot's area is its own image footprint, so it is
 *    read from each feature. Everything derived from area (density,
 *    hectare-equivalent) is therefore per-plot too, which is exactly the
 *    sample-plot statistic this endpoint exists to produce.
 *
 * 2. **`tile_name` is a real id.** Hexagon cells carry no id, so that path uses
 *    the array index and has to clear any stored selection whenever the set is
 *    refetched. `tile_name` is stable across refetches and is the same image
 *    name the rest of the system uses, so it doubles as the key for opening the
 *    plot's image on click.
 *
 * Note that DSP plots carry no `estimated_stems`, so `deriveCellStats`'
 * stem enrichment contributes nothing here and the stem properties/rows stay
 * absent throughout the UI — correct, rather than zeroed.
 *
 * @module collectionDspToPlots
 */

import { SQUARE_METERS_PER_HECTARE } from '@utils/measurementSystem';

import { deriveCellStats } from './hexagonCellStats';

/**
 * Per-object hectare equivalent for one plot, flattened as
 * `hectare_equivalent:<objectKey>` keys so `getPropertyValue`'s default case
 * resolves them and the classifier can band on a single object's density.
 *
 * The old DSP endpoint returned this per class; the collection endpoint doesn't,
 * so it's derived with the formula the API docs give for the plot-level figure:
 * `count / area_m2 * 10000`. A plot holding none of an object gets 0 rather than
 * being omitted — zero density is a real measurement for a sample plot, unlike a
 * mean, which stays null.
 *
 * @param {object} detections - per-object stats map
 * @param {number} areaM2 - the plot's footprint area
 * @returns {Object} e.g. { 'hectare_equivalent:<node uuid>': 1885.2 }
 */
const perObjectHectareEquivalent = (detections, areaM2) => {
	if (!(areaM2 > 0)) return {};
	const result = {};
	for (const [objectKey, det] of Object.entries(detections ?? {})) {
		result[`hectare_equivalent:${objectKey}`] =
			((det?.count ?? 0) / areaM2) * SQUARE_METERS_PER_HECTARE;
	}
	return result;
};

/**
 * @typedef {object} DspPlot
 * @property {string} id - the image name (`tile_name`), stable across refetches
 * @property {object} geometry - GeoJSON Polygon geometry (EPSG:3857), the image footprint
 * @property {object} detections - raw per-object stats, kept verbatim for the summary
 * @property {object} stats - flat, classifier-ready plot aggregate (see hexagonCellStats)
 */

/**
 * @param {Array<object>} features - GeoJSON Feature array from getCollectionDsp
 * @returns {DspPlot[]} plots with a resolvable `tile_name` (others are dropped —
 *   without an identity a plot can't be selected, opened, or keyed in a list)
 */
export const collectionDspToPlots = (features = []) =>
	features
		.filter(feature => feature?.properties?.tile_name)
		.map(feature => {
			const { tile_name: tileName, area_m2: areaM2, detections } = feature.properties;
			const area = areaM2 ?? 0;
			return {
				id: tileName,
				geometry: feature?.geometry,
				// Raw per-object stats, untouched, keyed by node uuid — the summary,
				// tooltip and report name each object through `objectLabel`.
				detections: detections ?? {},
				// Flat plot-level aggregate the matrix classifiers read via
				// getPropertyValue, with the plot's own footprint as `area`, plus
				// the per-object density keys derived below.
				stats: {
					...deriveCellStats(detections, area),
					...perObjectHectareEquivalent(detections, area),
				},
			};
		});

export default collectionDspToPlots;
